import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useEvvRecords,
    useExportEvv,
    useUpdateEvv,
    type EvvFilter,
    type EvvRecord,
    type EvvStatus,
} from '@/api/evv';
import { useClientNames } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useSettings } from '@/api/settings';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';
import { StatusBadge } from '@/components/ui/Badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { Alert } from '@/components/ui/Alert';
import { EvvLocationMap, EvvGpsCell } from './EvvLocationMap';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/*
 * EVV report — filters the evv_records surface by date range, client, staff,
 * status, and an exceptions-only toggle. The entire page is gated on the
 * practice-level toggle `settings.scheduling.evvEnabled`; when EVV is off the
 * page renders a disabled-state note rather than the table.
 *
 * Reviewer editing is limited to status + exception note (the two fields a
 * human dispositions after an Unverified check-out). GPS and timestamps come
 * from the check-in/check-out flow and aren't edited here.
 */

const STATUSES: EvvStatus[] = ['Open', 'Pending', 'Verified', 'Unverified', 'Exception'];

export function EvvReportPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));
    const canReview = useAuthStore((s) => s.hasPermission('evv_checkin'));

    const settings = useSettings();
    const evvEnabled = !!settings.data?.scheduling?.evvEnabled;

    const [filter, setFilter] = useState<EvvFilter>({});
    const [draft, setDraft]   = useState<EvvFilter>({});
    const [editing, setEditing] = useState<EvvRecord | null>(null);
    const [mapping, setMapping] = useState<EvvRecord | null>(null);

    const { data, isLoading, isError, error, isFetching } = useEvvRecords(filter);
    const exportM = useExportEvv();

    const clientsQuery = useClientNames();
    const staffQuery   = useUserDirectory();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const staffOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError surfaces below */ }
    }

    if (settings.isLoading) {
        return (
            <div className="p-6 max-w-[1400px] mx-auto">
                <div className="flex items-center gap-2 text-sm text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Visit Verification</h1>
                    <p className="text-xs text-text-subtle">
                        Check-in / check-out timestamps and GPS captured at the session; reviewers disposition exceptions here. Internal audit and billing-defense tool — not transmitted to state Medicaid aggregators.
                    </p>
                </div>
                {evvEnabled && canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            {!evvEnabled ? (
                <Alert variant="info">
                    EVV is turned off for this practice. Enable it in Settings &rarr; Scheduling to start capturing visits.
                </Alert>
            ) : (
                <>
                    <Card>
                        <CardBody>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                <label className="text-xs block">
                                    <span className="text-text-muted">Client</span>
                                    <select
                                        value={draft.clientId ?? ''}
                                        onChange={(e) => setDraft({ ...draft, clientId: e.target.value || undefined })}
                                        className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                                    >
                                        <option value="">All clients</option>
                                        {clientOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs block">
                                    <span className="text-text-muted">Staff</span>
                                    <select
                                        value={draft.staffId ?? ''}
                                        onChange={(e) => setDraft({ ...draft, staffId: e.target.value || undefined })}
                                        className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                                    >
                                        <option value="">All staff</option>
                                        {staffOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs block">
                                    <span className="text-text-muted">Status</span>
                                    <select
                                        value={draft.status ?? ''}
                                        onChange={(e) => setDraft({ ...draft, status: (e.target.value || undefined) as EvvStatus | undefined })}
                                        className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                                    >
                                        <option value="">All statuses</option>
                                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs block flex items-center gap-2 pb-1.5">
                                    <input
                                        type="checkbox"
                                        checked={!!draft.exceptionOnly}
                                        onChange={(e) => setDraft({ ...draft, exceptionOnly: e.target.checked || undefined })}
                                        className="w-3.5 h-3.5"
                                    />
                                    <span className="text-text-muted">Exceptions only</span>
                                </label>
                                <label className="text-xs block">
                                    <span className="text-text-muted">Date from</span>
                                    <Input
                                        type="date"
                                        min="1900-01-01"
                                        max={toIsoDate(new Date())}
                                        value={draft.dateFrom ?? ''}
                                        onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                        className="mt-1 h-8"
                                    />
                                </label>
                                <label className="text-xs block">
                                    <span className="text-text-muted">Date to</span>
                                    <Input
                                        type="date"
                                        min={draft.dateFrom || '1900-01-01'}
                                        max={toIsoDate(new Date())}
                                        value={draft.dateTo ?? ''}
                                        onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                        className="mt-1 h-8"
                                    />
                                </label>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                                <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                                <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                                {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                            </div>
                        </CardBody>
                    </Card>

                    {isError ? (
                        <Card>
                            <CardBody>
                                <p className="text-sm text-error font-medium">Couldn't load EVV records</p>
                                <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                            </CardBody>
                        </Card>
                    ) : isLoading || !data ? (
                        <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
                    ) : (
                        <Card>
                            <CardBody className="pt-0 overflow-x-auto">
                                {data.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No EVV records match the current filter.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Date</th>
                                                <th className="px-3 py-2 font-bold">Client</th>
                                                <th className="px-3 py-2 font-bold">Staff</th>
                                                <th className="px-3 py-2 font-bold">Service</th>
                                                <th className="px-3 py-2 font-bold">Check-in</th>
                                                <th className="px-3 py-2 font-bold">Check-out</th>
                                                <th className="px-3 py-2 font-bold text-right">Min</th>
                                                <th className="px-3 py-2 font-bold">GPS</th>
                                                <th className="px-3 py-2 font-bold">Status</th>
                                                <th className="px-3 py-2 font-bold">Exception</th>
                                                {canReview && <th className="px-3 py-2 font-bold"></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((r) => (
                                                <tr key={r.id} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2 whitespace-nowrap">{r.date ?? '—'}</td>
                                                    <td className="px-3 py-2">{r.client || '—'}</td>
                                                    <td className="px-3 py-2">{r.staff || '—'}</td>
                                                    <td className="px-3 py-2 text-xs">{r.service || '—'}</td>
                                                    <td className="px-3 py-2 text-xs tabular-nums">{formatTs(r.checkIn)}</td>
                                                    <td className="px-3 py-2 text-xs tabular-nums">{formatTs(r.checkOut)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{r.durMin || '—'}</td>
                                                    <td className="px-3 py-2 text-xs">
                                                        <EvvGpsCell record={r} onOpen={() => setMapping(r)} />
                                                    </td>
                                                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                                    <td className="px-3 py-2 text-xs text-text-muted max-w-[240px] truncate" title={r.exception ?? undefined}>
                                                        {r.exception || '—'}
                                                    </td>
                                                    {canReview && (
                                                        <td className="px-3 py-2 text-right">
                                                            <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                                                                <Pencil className="w-3 h-3" />
                                                                Edit
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </>
            )}

            {editing && (
                <EvvEditDialog
                    record={editing}
                    onClose={() => setEditing(null)}
                />
            )}

            {mapping && (
                <EvvLocationMap
                    record={mapping}
                    onClose={() => setMapping(null)}
                />
            )}
        </div>
    );
}

function formatTs(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

function EvvEditDialog({
    record, onClose,
}: { record: EvvRecord; onClose: () => void }) {
    const [status, setStatus]       = useState<EvvStatus>(record.status);
    const [exception, setException] = useState<string>(record.exception ?? '');
    const [error, setError]         = useState<string | null>(null);
    const updateM = useUpdateEvv();

    async function save() {
        setError(null);
        try {
            await updateM.mutateAsync({
                id: record.id,
                updates: {
                    status,
                    exception: exception.trim() ? exception.trim() : null,
                },
            });
            onClose();
        } catch (err) {
            setError(extractErrorMessage(err, 'Save failed.'));
        }
    }

    return (
        <Dialog open onOpenChange={(open) => { if (!open && !updateM.isPending) onClose(); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Update EVV record</DialogTitle>
                    <DialogDescription>
                        {record.client || 'Unknown client'}{record.date ? ` — ${record.date}` : ''}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <label className="text-xs block">
                        <span className="text-text-muted">Status</span>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value as EvvStatus)}
                            className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                        >
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </label>
                    <label className="text-xs block">
                        <span className="text-text-muted">Exception note</span>
                        <textarea
                            value={exception}
                            onChange={(e) => setException(e.target.value)}
                            rows={3}
                            placeholder="Describe the mismatch (optional)"
                            className="mt-1 w-full px-2 py-1.5 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                        />
                    </label>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={updateM.isPending}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={save} disabled={updateM.isPending}>
                        {updateM.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
